import { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Switch,
  Select,
  Button,
  message,
  Typography,
  Divider,
  Row,
  Col,
  InputNumber,
  Spin,
  Modal,
  Input,
} from 'antd';
import { ExclamationCircleOutlined, DownloadOutlined, DeleteOutlined, ClearOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { settingsApi } from '@tradermind/shared/api/settingsApi';
import type { UserSettings } from '@tradermind/shared/api/settingsApi';
import { useAppStore, useAuthStore } from '@tradermind/shared/stores';
import { LANGUAGE_LABELS, type SupportedLanguage } from '@tradermind/shared/i18n';

const { Title, Text } = Typography;

export default function SettingsPage() {
  const { t } = useTranslation('settings');
  const { theme, language, setTheme, setLanguage } = useAppStore();
  const queryClient = useQueryClient();

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: settingsApi.getSettings,
  });

  const [localSettings, setLocalSettings] = useState<UserSettings | null>(null);

  useEffect(() => {
    if (settingsData?.data) {
      setLocalSettings(settingsData.data);
    }
  }, [settingsData]);

  const updateMutation = useMutation({
    mutationFn: settingsApi.updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      message.success(t('save_success'));
    },
    onError: () => {
      message.error(t('save_failed'));
    },
  });

  const handleSave = () => {
    if (localSettings) {
      updateMutation.mutate(localSettings);
    }
  };

  const updateLocalSetting = (updates: Partial<UserSettings>) => {
    setLocalSettings(prev => prev ? { ...prev, ...updates } : null);
  };

  const [isExporting, setIsExporting] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const logout = useAuthStore((state) => state.logout);

  // 导出数据
  const handleExportData = async () => {
    try {
      setIsExporting(true);
      const response = await settingsApi.exportData();
      
      // 创建下载链接
      const blob = response instanceof Blob ? response : new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tradermind_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      message.success(t('export_success'));
    } catch (error) {
      message.error(t('export_failed'));
    } finally {
      setIsExporting(false);
    }
  };

  // 清除缓存
  const handleClearCache = () => {
    Modal.confirm({
      title: t('clear_cache'),
      icon: <ExclamationCircleOutlined />,
      content: t('clear_cache_confirm'),
      okText: t('common:confirm'),
      cancelText: t('common:cancel'),
      onOk: () => {
        localStorage.clear();
        queryClient.clear();
        message.success(t('cache_cleared'));
      },
    });
  };

  // 删除账户确认
  const showDeleteConfirm = () => {
    Modal.confirm({
      title: t('delete_account'),
      icon: <ExclamationCircleOutlined />,
      content: t('delete_account_warning'),
      okText: t('common:continue'),
      okType: 'danger',
      cancelText: t('common:cancel'),
      onOk: () => {
        setDeletePassword('');
        setDeleteModalVisible(true);
      },
    });
  };

  // 执行删除账户
  const handleDeleteAccount = async () => {
    if (!deletePassword) {
      message.error(t('enter_password'));
      return;
    }

    try {
      setIsDeleting(true);
      await settingsApi.deleteAccount({
        password: deletePassword,
        confirmation: 'DELETE',
      });
      setDeleteModalVisible(false);
      message.success(t('account_deleted'));
      logout();
    } catch (error: any) {
      message.error(error?.response?.data?.message || t('common:error.unknown'));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading || !localSettings) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Title level={4}>{t('title')}</Title>

      <Row gutter={24}>
        <Col xs={24} lg={12}>
          <Card title={t('appearance')} style={{ marginBottom: 24 }}>
            <Form layout="vertical">
              <Form.Item label={t('theme')}>
                <Select
                  value={theme}
                  onChange={setTheme}
                  options={[
                    { value: 'light', label: t('theme_light') },
                    { value: 'dark', label: t('theme_dark') },
                    { value: 'system', label: t('theme_system') },
                  ]}
                />
              </Form.Item>

              <Form.Item label={t('language')}>
                <Select
                  value={language}
                  onChange={(val) => setLanguage(val as SupportedLanguage)}
                  options={Object.entries(LANGUAGE_LABELS).map(([value, label]) => ({
                    value,
                    label,
                  }))}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card title={t('notifications')}>
            <Form layout="vertical">
              <Form.Item label={t('email_notifications')}>
                <Switch
                  checked={localSettings.email_notifications}
                  onChange={(checked) =>
                    updateLocalSetting({ email_notifications: checked })
                  }
                />
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {t('email_notifications_desc')}
                </Text>
              </Form.Item>

              <Form.Item label={t('push_notifications')}>
                <Switch
                  checked={localSettings.push_notifications}
                  onChange={(checked) =>
                    updateLocalSetting({ push_notifications: checked })
                  }
                />
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {t('push_notifications_desc')}
                </Text>
              </Form.Item>

              <Form.Item label={t('weekly_report')}>
                <Switch
                  checked={localSettings.weekly_report}
                  onChange={(checked) =>
                    updateLocalSetting({ weekly_report: checked })
                  }
                />
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {t('weekly_report_desc')}
                </Text>
              </Form.Item>

              {localSettings.weekly_report && (
                <>
                  <Form.Item label={t('weekly_report_day')}>
                    <Select
                      value={localSettings.weekly_report_day || 1}
                      onChange={(value) => updateLocalSetting({ weekly_report_day: value })}
                      style={{ width: 150 }}
                      options={[
                        { value: 1, label: t('day_monday') },
                        { value: 2, label: t('day_tuesday') },
                        { value: 3, label: t('day_wednesday') },
                        { value: 4, label: t('day_thursday') },
                        { value: 5, label: t('day_friday') },
                        { value: 6, label: t('day_saturday') },
                        { value: 7, label: t('day_sunday') },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item label={t('weekly_report_hour')}>
                    <Select
                      value={localSettings.weekly_report_hour || 9}
                      onChange={(value) => updateLocalSetting({ weekly_report_hour: value })}
                      style={{ width: 150 }}
                      options={Array.from({ length: 24 }, (_, i) => ({
                        value: i,
                        label: `${i.toString().padStart(2, '0')}:00`,
                      }))}
                    />
                  </Form.Item>

                  <Form.Item label={t('weekly_report_method')}>
                    <Select
                      value={localSettings.weekly_report_method || 'email'}
                      onChange={(value) => updateLocalSetting({ weekly_report_method: value })}
                      style={{ width: 150 }}
                      options={[
                        { value: 'email', label: t('weekly_report_method_email') },
                        { value: 'push', label: t('weekly_report_method_push') },
                        { value: 'both', label: t('weekly_report_method_both') },
                      ]}
                    />
                  </Form.Item>
                </>
              )}

            </Form>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('cooldown')}>
            <Form layout="vertical">
              <Form.Item label={t('auto_trigger_cooldown')}>
                <Switch
                  checked={localSettings.auto_trigger_cooldown}
                  onChange={(checked) =>
                    updateLocalSetting({ auto_trigger_cooldown: checked })
                  }
                />
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  {t('auto_trigger_cooldown_desc')}
                </Text>
              </Form.Item>

              <Divider />

              <Form.Item label={t('consecutive_loss_threshold')}>
                <InputNumber
                  value={localSettings.consecutive_loss_threshold}
                  onChange={(value) =>
                    updateLocalSetting({ consecutive_loss_threshold: value || 3 })
                  }
                  min={1}
                  max={10}
                  addonAfter={t('common:times')}
                  style={{ width: 150 }}
                />
                <div>
                  <Text type="secondary">{t('consecutive_loss_threshold_desc')}</Text>
                </div>
              </Form.Item>

              <Form.Item label={t('default_cooldown_duration')}>
                <InputNumber
                  value={localSettings.default_cooldown_duration}
                  onChange={(value) =>
                    updateLocalSetting({ default_cooldown_duration: value || 30 })
                  }
                  min={5}
                  max={120}
                  addonAfter={t('default_cooldown_duration_unit')}
                  style={{ width: 150 }}
                />
              </Form.Item>
            </Form>
          </Card>

          <Card title={t('r_multiple_settings')} style={{ marginTop: 24 }}>
            <Form layout="vertical">
              <Form.Item label={t('breakeven_threshold')}>
                <InputNumber
                  value={localSettings.breakeven_threshold}
                  onChange={(value) =>
                    updateLocalSetting({ breakeven_threshold: value || 0.2 })
                  }
                  min={0}
                  max={1}
                  step={0.05}
                  style={{ width: 150 }}
                />
                <div>
                  <Text type="secondary">{t('breakeven_threshold_desc')}</Text>
                </div>
                <div>
                  <Text type="secondary">{t('breakeven_threshold_hint')}</Text>
                </div>
              </Form.Item>
            </Form>
          </Card>

          <Card title={t('data_management')} style={{ marginTop: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <Button 
                icon={<DownloadOutlined />}
                onClick={handleExportData}
                loading={isExporting}
              >
                {t('export_data')}
              </Button>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{t('export_data_desc')}</Text>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Button 
                icon={<ClearOutlined />}
                onClick={handleClearCache}
              >
                {t('clear_cache')}
              </Button>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{t('clear_cache_desc')}</Text>
              </div>
            </div>

            <div>
              <Button 
                danger 
                icon={<DeleteOutlined />}
                onClick={showDeleteConfirm}
              >
                {t('delete_account')}
              </Button>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{t('delete_account_desc')}</Text>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Button 
          type="primary" 
          size="large" 
          onClick={handleSave}
          loading={updateMutation.isPending}
        >
          {t('common:save')}
        </Button>
      </div>

      <Modal
        title={t('delete_account')}
        open={deleteModalVisible}
        onCancel={() => setDeleteModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setDeleteModalVisible(false)}>
            {t('common:cancel')}
          </Button>,
          <Button
            key="delete"
            danger
            type="primary"
            loading={isDeleting}
            onClick={handleDeleteAccount}
          >
            {t('delete_account')}
          </Button>,
        ]}
      >
        <p style={{ color: '#ff4d4f', marginBottom: 16 }}>
          {t('delete_account_confirm')}
        </p>
        <Input.Password
          placeholder={t('enter_password')}
          value={deletePassword}
          onChange={(e) => setDeletePassword(e.target.value)}
        />
      </Modal>
    </div>
  );
}
